<template>
  <div
    id="support-panel"
    ref="supportPanel"
    :class="[isOpenPanel ? 'translate-x-0' : 'translate-x-[calc(100%-1.5rem)]']"
    class="z-30 fixed bottom-10 right-0 hidden min-h-[201px] w-56 justify-stretch overflow-hidden rounded-l bg-white shadow transition-all duration-300 sm:flex"
  >
    <div
      id="open_support"
      class="flex w-6 cursor-pointer flex-col items-center justify-between bg-black-100 py-2"
      @click="isOpenPanel = !isOpenPanel"
    >
      <UIIcon v-show="isOpenPanel" name="big-close" size="xs" class="text-white" />
      <UIIcon v-show="!isOpenPanel" name="chevron-big-left" size="xs" class="text-white" />
      <p class="text-caption-2 -rotate-90 whitespace-nowrap pl-12 text-black-20">Help center</p>
    </div>
    <Transition name="fade" mode="out-in">
      <SupportPanelSection v-if="selectedSection === 'updates'" @back="selectedSection = null">
        <template #title> Product updates </template>
        <SupportPanelUpdate
          v-for="update in supportPanelUpdatesToShow"
          :key="update.id"
          class="mb-2"
          :checked="uiStore.getIsTourCompleted(update.id)"
          @check="update.check"
        >
          <template #title>{{ update.title }} </template>
          <template #date> {{ update.date }}</template>
          <template #description> {{ update.description }}</template>
        </SupportPanelUpdate>
      </SupportPanelSection>

      <div v-else class="p-2 w-full">
        <SupportPanelItem @select="selectedSection = 'updates'">
          <template #title> Product updates </template>
          <template #description>
            New features <br />
            announcements and tours.
          </template>
          <template #count> {{ numberOfUncheckedTours }} </template>
        </SupportPanelItem>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { TOURS_CODES } from '~/constants'
import useUiStore from '@/store/ui'
import useAuthStore from '~/store/auth'

const uiStore = useUiStore()
const authStore = useAuthStore()
const route = useRoute()
const { getTours } = authStore

const isOpenPanel = ref(false)
const supportPanel = ref<HTMLElement>()
const toursList = ref<TOURS_CODES[]>([])

const selectedSection = ref<'updates' | 'support' | null>(null)

const shownToursList = {
  [TOURS_CODES.DASHBOARD]: { tourName: TOURS_CODES.DASHBOARD, routeName: 'dashboard' },
  [TOURS_CODES.EARNINGS]: { tourName: TOURS_CODES.EARNINGS, routeName: 'earnings' },
}

const supportPanelUpdates = [
  {
    id: TOURS_CODES.DASHBOARD,
    title: 'Updated dashboard',
    date: 'Aug 19, 2025',
    description: 'Now you can easily access featured properties and marketing materials right away. ⚡️',
    check: () => showTourAgain(TOURS_CODES.DASHBOARD),
  },
  {
    id: TOURS_CODES.EARNINGS,
    title: 'Welcome to the Earnings',
    date: 'Aug 30, 2024',
    description: 'A quick look of what you have on the Earnings page',
    check: () => showTourAgain(TOURS_CODES.EARNINGS),
  },
]

const numberOfUncheckedTours = computed(() => {
  return supportPanelUpdates.filter(update => !uiStore.getIsTourCompleted(update.id)).length
})

const supportPanelUpdatesToShow = computed(() =>
  supportPanelUpdates
    .filter(update => (update.condition ? update.condition() : true))
    .sort((a, b) => {
      // sort by user's tours, unchecked tours go first
      return Number(uiStore.getIsTourCompleted(a.id)) - Number(uiStore.getIsTourCompleted(b.id))
    }),
)

onClickOutside(supportPanel, () => {
  if (isOpenPanel.value) {
    selectedSection.value = null
    isOpenPanel.value = false
  }
})

const showTourAgain = async (tour: TOURS_CODES) => {
  isOpenPanel.value = false
  selectedSection.value = null
  toursList.value = getTours().filter(item => item !== shownToursList[tour]?.tour)

  authStore.setActiveTourId(tour)

  await nextTick()

  if (route.name !== shownToursList[tour]?.routeName) {
    await navigateTo({
      name: shownToursList[tour]?.routeName,
    })
  }
}
</script>
